import { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useHostSession } from "./useHostSession.js";
import type { PlayerInfo } from "./types.js";
import { useT, type Translate } from "../../i18n/index.js";

const COPY_FEEDBACK_MS = 2000;

type CopyStatus = "idle" | "copied" | "failed";

function colorLabel(t: Translate, color: string): string {
  if (color === "red") return t("common.red");
  if (color === "blue") return t("common.blue");
  if (color === "yellow") return t("common.yellow");
  if (color === "green") return t("common.green");
  return color;
}

function useCopyToClipboard(): [CopyStatus, (text: string) => void] {
  const [status, setStatus] = useState<CopyStatus>("idle");
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (resetTimer.current) clearTimeout(resetTimer.current);
    };
  }, []);

  const copy = (text: string) => {
    const scheduleReset = () => {
      if (resetTimer.current) clearTimeout(resetTimer.current);
      resetTimer.current = setTimeout(() => setStatus("idle"), COPY_FEEDBACK_MS);
    };
    const fail = () => {
      setStatus("failed");
      scheduleReset();
    };
    // The Clipboard API is only exposed in a secure context, so it is missing
    // whenever the host screen is opened over plain HTTP on the LAN. Reading
    // `.writeText` off `undefined` would throw before any promise exists, and
    // an error thrown from an event handler escapes the error boundary.
    const write = navigator.clipboard?.writeText;
    if (!write) {
      fail();
      return;
    }
    try {
      write
        .call(navigator.clipboard, text)
        .then(() => {
          setStatus("copied");
          scheduleReset();
        })
        .catch(fail);
    } catch {
      fail();
    }
  };

  return [status, copy];
}

const s = {
  page: {
    minHeight: "100svh",
    background: "var(--bg)",
    color: "var(--text)",
    fontFamily: "inherit",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    padding: "var(--space-xl) var(--space-md)",
    gap: "var(--space-xl)",
  },
  header: {
    textAlign: "center" as const,
    width: "100%",
    maxWidth: "640px",
  },
  appName: {
    margin: "0 0 0.25rem",
    fontSize: "var(--text-sm)",
    fontWeight: 700,
    color: "var(--brand)",
    textTransform: "uppercase" as const,
    letterSpacing: "0.1em",
  },
  title: {
    margin: "0 0 var(--space-xs)",
    fontSize: "var(--text-xl)",
    fontWeight: 700,
    color: "var(--text)",
  },
  status: {
    margin: 0,
    fontSize: "var(--text-xs)",
    color: "var(--text-dim)",
  },
  pinBlock: {
    background: "var(--surface)",
    borderRadius: "var(--radius-xl)",
    padding: "var(--space-xl) var(--space-xl)",
    textAlign: "center" as const,
    border: "2px solid var(--border)",
    boxShadow: "var(--shadow-lg)",
    width: "100%",
    maxWidth: "480px",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    gap: "var(--space-md)",
  },
  pinLabel: {
    margin: 0,
    fontSize: "var(--text-sm)",
    color: "var(--text-dim)",
    textTransform: "uppercase" as const,
    letterSpacing: "0.1em",
    fontWeight: 600,
  },
  pin: {
    margin: 0,
    fontSize: "var(--text-3xl)",
    fontFamily: "ui-monospace, 'Courier New', monospace",
    letterSpacing: "0.3em",
    color: "var(--brand)",
    fontWeight: 800,
    lineHeight: 1,
  },
  pinInstructions: {
    margin: 0,
    fontSize: "var(--text-sm)",
    color: "var(--text-mid)",
    fontWeight: 500,
  },
  qrWrapper: {
    background: "#fff",
    borderRadius: "var(--radius-md)",
    padding: "0.75rem",
    lineHeight: 0,
    boxShadow: "var(--shadow-sm)",
  },
  qrLabel: {
    margin: "0.5rem 0 0",
    fontSize: "var(--text-xs)",
    color: "var(--text-dim)",
  },
  section: {
    width: "100%",
    maxWidth: "640px",
  },
  sectionTitle: {
    fontSize: "var(--text-sm)",
    color: "var(--text-mid)",
    fontWeight: 600,
    textTransform: "uppercase" as const,
    letterSpacing: "0.08em",
    marginBottom: "var(--space-sm)",
    marginTop: 0,
  },
  playerList: {
    listStyle: "none",
    padding: 0,
    margin: 0,
    display: "flex",
    flexDirection: "column" as const,
    gap: "0.5rem",
  },
  playerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "var(--surface)",
    padding: "0.875rem var(--space-md)",
    borderRadius: "var(--radius-md)",
    border: "1px solid var(--border)",
    boxShadow: "var(--shadow-sm)",
  },
  playerName: {
    fontWeight: 600,
    fontSize: "var(--text-sm)",
  },
  kickBtn: {
    background: "transparent",
    border: "1.5px solid var(--error)",
    color: "var(--error)",
    padding: "0.375rem 0.875rem",
    borderRadius: "var(--radius-sm)",
    cursor: "pointer",
    fontSize: "var(--text-xs)",
    fontWeight: 600,
    minHeight: "36px",
    transition: "background var(--duration-fast) var(--ease-out)",
  },
  emptyMsg: {
    color: "var(--text-dim)",
    fontStyle: "italic" as const,
    fontSize: "var(--text-sm)",
    padding: "var(--space-lg)",
    textAlign: "center" as const,
    background: "var(--surface)",
    borderRadius: "var(--radius-md)",
    border: "1px dashed var(--border)",
  },
  controls: {
    display: "flex",
    gap: "var(--space-md)",
    flexWrap: "wrap" as const,
    width: "100%",
    maxWidth: "640px",
  },
  settings: {
    width: "100%",
    maxWidth: "640px",
    display: "grid",
    gap: "0.65rem",
  },
  settingRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "var(--space-md)",
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-md)",
    padding: "0.8rem var(--space-md)",
    boxShadow: "var(--shadow-sm)",
    fontSize: "var(--text-sm)",
    fontWeight: 700,
  },
  checkbox: {
    width: "1.2rem",
    height: "1.2rem",
    accentColor: "var(--brand)",
  },
  pattern: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-md)",
    padding: "0.85rem var(--space-md)",
    boxShadow: "var(--shadow-sm)",
  },
  patternLabel: {
    fontSize: "var(--text-sm)",
    color: "var(--text-mid)",
    fontWeight: 700,
    marginRight: "auto",
  },
  patternDot: (color: string) => ({
    width: "2rem",
    height: "2rem",
    borderRadius: "50%",
    border: "2px solid var(--border)",
    background:
      color === "red" ? "var(--opt-0)" :
      color === "blue" ? "var(--opt-1)" :
      color === "yellow" ? "var(--opt-2)" :
      "var(--opt-3)",
  }),
  btn: (variant: "primary" | "secondary" | "danger") => ({
    flex: 1,
    minWidth: "140px",
    padding: "1rem var(--space-lg)",
    borderRadius: "var(--radius-md)",
    border: "none",
    fontSize: "var(--text-base)",
    fontWeight: 700,
    cursor: "pointer",
    minHeight: "56px",
    background:
      variant === "primary" ? "var(--brand)" :
      variant === "secondary" ? "var(--bg-deep)" :
      "var(--error)",
    color: variant === "secondary" ? "var(--text)" : "#fff",
    boxShadow: "var(--shadow-sm)",
    transition: "background var(--duration-fast) var(--ease-out)",
  }),
  disabledBtn: {
    opacity: 0.4,
    cursor: "not-allowed" as const,
  },
};

interface Props {
  sessionId: string;
  onGameStarted?: () => void;
}

export function HostLobby({ sessionId, onGameStarted }: Props) {
  const t = useT();
  const {
    phase,
    players,
    pin,
    isConnected,
    snapshot,
    lockLobby,
    kickPlayer,
    updateSettings,
    startGame,
  } =
    useHostSession(sessionId);
  const [copyStatus, copyJoinUrl] = useCopyToClipboard();

  useEffect(() => {
    if (phase !== "open_lobby" && phase !== "locked_lobby" && phase !== "created" && onGameStarted) {
      onGameStarted();
    }
  }, [phase, onGameStarted]);

  const isLobbyLocked = phase === "locked_lobby";
  const hasPlayers = players.filter((p) => !p.isKicked).length > 0;
  const activePlayers = players.filter((p) => !p.isKicked);
  const joinUrl = pin ? `${window.location.origin}/join/${pin}` : "";
  const settings = snapshot?.settings;

  return (
    <div style={s.page}>
      <header style={s.header}>
        <p style={s.appName}>HootApp</p>
        <h1 style={s.title}>{t("hostLobby.waitingForPlayers")}</h1>
        <p style={s.status}>{isConnected ? t("hostLobby.connected") : t("hostLobby.connecting")}</p>
      </header>

      <div style={s.pinBlock}>
        <p style={s.pinLabel}>{t("hostLobby.gameCode")}</p>
        <p style={s.pin} aria-label={`${t("hostLobby.gameCode")}: ${pin}`}>
          {pin || "------"}
        </p>
        <p style={s.pinInstructions}>
          {t("hostLobby.share")} <strong>{joinUrl || `${window.location.origin}/join`}</strong>
        </p>
        {joinUrl && (
          <button
            type="button"
            className={`copy-link-button${copyStatus === "copied" ? " is-copied" : ""}`}
            onClick={() => copyJoinUrl(joinUrl)}
            aria-live="polite"
          >
            {copyStatus === "copied"
              ? t("hostLobby.linkCopied")
              : copyStatus === "failed"
                ? t("hostLobby.copyFailed")
                : t("hostLobby.copyLink")}
          </button>
        )}
        {joinUrl && (
          <div>
            <div style={s.qrWrapper}>
              <QRCodeSVG
                value={joinUrl}
                size={160}
                level="M"
                aria-label={t("hostLobby.qrLabel", { url: joinUrl })}
              />
            </div>
            <p style={s.qrLabel}>{t("hostLobby.scanToJoin")}</p>
          </div>
        )}
      </div>

      <section style={s.section} aria-label={t("hostLobby.playersInLobby")}>
        <h2 style={s.sectionTitle}>
          {t("hostLobby.players", { count: activePlayers.length })}
        </h2>
        {activePlayers.length === 0 ? (
          <p style={s.emptyMsg}>{t("hostLobby.noPlayers")}</p>
        ) : (
          <ul style={s.playerList}>
            {activePlayers.map((p: PlayerInfo) => (
              <li key={p.id} style={s.playerRow}>
                <span style={s.playerName}>{p.nickname}</span>
                <button
                  className="btn-quiet-danger"
                  style={s.kickBtn}
                  onClick={() => kickPlayer(p.id)}
                  aria-label={t("hostLobby.removePlayer", { nickname: p.nickname })}
                >
                  {t("hostLobby.remove")}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section style={s.settings} aria-label={t("hostLobby.settings")}>
        <h2 style={s.sectionTitle}>{t("hostLobby.settings")}</h2>
        <label style={s.settingRow}>
          {t("hostLobby.twoStepJoin")}
          <input
            type="checkbox"
            checked={settings?.requireTwoStepJoin ?? false}
            onChange={(e) => updateSettings({ requireTwoStepJoin: e.target.checked })}
            style={s.checkbox}
          />
        </label>
        {(settings?.requireTwoStepJoin ?? false) && (
          <div style={s.pattern} aria-label={t("hostLobby.pattern")}>
            <span style={s.patternLabel}>{t("hostLobby.pattern")}</span>
            {(settings?.twoStepPattern ?? []).map((color, index) => (
              <span
                key={`${color}-${index}`}
                title={colorLabel(t, color)}
                aria-label={colorLabel(t, color)}
                style={s.patternDot(color)}
              />
            ))}
          </div>
        )}
        <label style={s.settingRow}>
          {t("hostLobby.randomQuestionOrder")}
          <input
            type="checkbox"
            checked={settings?.randomizeOrder ?? false}
            onChange={(e) => updateSettings({ randomizeOrder: e.target.checked })}
            style={s.checkbox}
          />
        </label>
        <label style={s.settingRow}>
          {t("hostLobby.randomAnswerOrder")}
          <input
            type="checkbox"
            checked={settings?.randomizeAnswers ?? false}
            onChange={(e) => updateSettings({ randomizeAnswers: e.target.checked })}
            style={s.checkbox}
          />
        </label>
        <label style={s.settingRow}>
          {t("hostLobby.showLeaderboard")}
          <input
            type="checkbox"
            checked={settings?.showLeaderboard ?? true}
            onChange={(e) => updateSettings({ showLeaderboard: e.target.checked })}
            style={s.checkbox}
          />
        </label>
      </section>

      <div style={s.controls} role="group" aria-label={t("hostLobby.controls")}>
        <button
          className="btn-tactile"
          style={s.btn("secondary")}
          onClick={() => lockLobby(!isLobbyLocked)}
          aria-pressed={isLobbyLocked}
        >
          {isLobbyLocked ? t("hostLobby.unlock") : t("hostLobby.lock")}
        </button>
        <button
          className="btn-tactile"
          style={{ ...s.btn("primary"), ...(hasPlayers ? {} : s.disabledBtn) }}
          onClick={hasPlayers ? startGame : undefined}
          disabled={!hasPlayers}
          aria-disabled={!hasPlayers}
        >
          {t("hostLobby.start")}
        </button>
      </div>
    </div>
  );
}
